import Data from 'data'

import { getIconPath } from '@/common'
import { ItemFlags } from '@/data/enums'
import { ItemDefinition, Range, UniqueRandomMod } from '@/data/types'
import { gemFormatProperties, gemGetProperties, gemItemIcon, gemTemplateProps } from '@/logic/gem'
import {
  itemCreate,
  itemEssenceMods,
  itemFormatProperties,
  itemGetBaseColor,
  itemGetName,
  itemGetProperties,
  itemGetStats,
  itemTemplateProps,
  itemTranslationDomain,
  itemUniqueProps,
  uniqueGroupName
} from '@/logic/item'
import { modStats, StatMap, statsExtend } from '@/logic/mods'
import { TooltipLine } from '@/types/common'
import { Gem, Item, ItemTooltipOptions } from '@/types/item'
import { FormatLine, FormatLineBr, FormatLines, GameTooltip, statLines, TooltipImage } from './common'

const Separator = () => <div className="poe-separator"></div>

const FormatSection = ({ lines }: { lines: TooltipLine[] | undefined }) =>
  !!lines && (
    <>
      <FormatLines lines={lines} />
      {lines.length > 0 && <Separator />}
    </>
  )

function ItemInfluence({ influence }: { influence: ItemFlags | undefined }) {
  if (!influence) return null
  const flags = [...Array(13)].map((_, i) => i + 1).filter((i) => influence & (1 << i))
  if (!flags.length) return null
  return (
    <>
      <div className={`poe-influence poe-influence-left poe-influence-${flags[0]}`} />
      <div className={`poe-influence poe-influence-right poe-influence-${flags[1] || flags[0]}`} />
    </>
  )
}

const ItemSingleHeader = ({ name, influence }: { name: string; influence?: ItemFlags }) => (
  <div className="poe-itemHeader">
    <ItemInfluence influence={influence} />
    <div className="poe-itemName poe-typeLine">
      <span className="poe-lc">{name}</span>
    </div>
  </div>
)
const ItemDoubleHeader = ({ name, base, influence }: { name: string; base: string; influence?: ItemFlags }) => (
  <div className="poe-itemHeader poe-doubleLine">
    <ItemInfluence influence={influence} />
    <div className="poe-itemName">
      <span className="poe-lc">{name}</span>
    </div>
    <div className="poe-itemName poe-typeLine">
      <span className="poe-lc">{base}</span>
    </div>
  </div>
)

const ItemHeader = ({ item }: { item: Item }) => {
  return ['rare', 'unique', 'relic'].includes(item.rarity) ? (
    <ItemDoubleHeader name={itemGetName(item)} base={Data.items[item.base].name} influence={item.flags} />
  ) : (
    <ItemSingleHeader name={itemGetName(item)} influence={item.flags} />
  )
}

const ItemPropertyList = ({
  props
}: {
  props: {
    properties: TooltipLine[]
    requirements: TooltipLine[]
  }
}) => (
  <>
    <FormatSection lines={props.properties} />
    <FormatSection lines={props.requirements} />
  </>
)

const ItemProperties = ({ item, template }: { item: Item; template?: boolean }) => {
  const properties = template ? itemTemplateProps(item) : itemGetProperties(item, itemGetStats(item))
  return <ItemPropertyList props={itemFormatProperties(item, properties, true)} />
}

const ItemTemplateProperties = ({ name, variant }: { name: string; variant?: number }) => (
  <ItemPropertyList props={itemUniqueProps(name, variant)!} />
)

const ItemEnchant = ({ item, options }: { item: Item; options?: ItemTooltipOptions }) => (
  <FormatSection
    lines={statLines(
      'enchantMod',
      item.stats.enchant,
      options?.domain || itemTranslationDomain(Data.items[item.base]),
      options?.explicit
    )}
  />
)

function ItemClusterJewel({ item, options }: { item: Item; options?: ItemTooltipOptions }) {
  const base = Data.items[item.base]
  if (!base.cluster_jewel || !item.clusterJewel) return null
  if (item.unique) return null

  const lines = statLines('enchantMod', {
    local_jewel_expansion_passive_node_count: item.clusterJewel.nodes,
    local_jewel_expansion_jewels_count:
      base.cluster_jewel.size === 'Large' ? 2 : base.cluster_jewel.size === 'Medium' ? 1 : 0
  })

  const skill = Data.passive_skills[item.clusterJewel.skill]
  if (skill) {
    lines.push(...statLines('enchantMod', skill.stats, 'global', undefined, 'Added Small Passive Skills grant: '))
  }

  return <FormatSection lines={lines} />
}

function ItemImplicits({ item, options }: { item: Item; options?: ItemTooltipOptions }) {
  const style = 'implicitMod'
  const base = Data.items[item.base]
  const lines = statLines(style, item.stats.implicit, options?.domain || itemTranslationDomain(base), options?.implicit)
  if (base.properties && base.properties.description) {
    lines.push({ text: base.properties.description, type: 'implicitMod' })
  }
  const essence = Data.essences[item.base]
  if (essence) {
    if (essence.item_level_restriction) {
      lines.push({
        text: `Properties restricted to level ${essence.item_level_restriction} and below`,
        type: 'implicitMod'
      })
    }
    lines.push({ text: '', type: 'displayProperty' })
    for (const { name, mod } of itemEssenceMods(essence)) {
      if (!Data.mods[mod]) continue
      const sub = statLines('implicitMod', modStats(Data.mods[mod]), options?.domain)
      if (sub[0]) sub[0].text = `${name}: ${sub[0].text}`
      lines.push(...sub)
    }
  }
  return <FormatSection lines={lines} />
}

function ItemTemplateImplicits({ base, mods }: { base: ItemDefinition; mods?: UniqueRandomMod[] }) {
  const stats: StatMap = {}
  for (const mod of base.implicits) {
    statsExtend(stats, modStats(Data.mods[mod]))
  }
  const lines = statLines('implicitMod', stats, itemTranslationDomain(base))
  const randomMods = mods?.filter((mod) => mod.statType === 'implicit' || mod.statType === 'enchant')
  if (base.properties && base.properties.description) {
    lines.push({ text: base.properties.description, type: 'implicitMod' })
  }
  if (randomMods?.length) {
    lines.push(
      ...randomMods.flatMap((mod) =>
        uniqueGroupName(mod).map((text) => ({
          text,
          type: `${mod.statType ?? 'implicit'}Mod`
        }))
      )
    )
  }
  const essence = Data.essences[base.id]
  if (essence) {
    if (essence.item_level_restriction) {
      lines.push({
        text: `Properties restricted to level ${essence.item_level_restriction} and below`,
        type: 'implicitMod'
      })
    }
    lines.push({ text: '', type: 'displayProperty' })
    for (const { name, mod } of itemEssenceMods(essence)) {
      if (!Data.mods[mod]) continue
      const sub = statLines('implicitMod', modStats(Data.mods[mod]))
      if (sub[0]) sub[0].text = `${name}: ${sub[0].text}`
      lines.push(...sub)
    }
  }

  return <FormatSection lines={lines} />
}

function ItemExplicits({ item, options }: { item: Item; options?: ItemTooltipOptions }) {
  const explicit = statsExtend({}, item.stats.explicit, item.stats.unique, item.stats.custom)
  const base = Data.items[item.base]
  if (base?.tattoo) {
    const tattoo = Data.tattoos[base.tattoo.id]
    statsExtend(explicit, tattoo.stats)
  }
  const domain = options?.domain ?? itemTranslationDomain(base)
  const lines = [
    ...statLines('fracturedMod', item.stats.fractured, domain, options?.fractured),
    ...statLines('explicitMod', explicit, domain, options?.explicit),
    ...(item.customText?.map((text) => ({ text, type: 'explicitMod' })) || []),
    ...statLines('craftedMod', item.stats.crafted, domain, options?.crafted),
    ...[...Array(item.veiledMods || 0)].map(() => ({
      text: 'Veiled Affix',
      type: 'veiledMod'
    }))
  ]
  if (item.flags & ItemFlags.Corrupted) lines.push({ text: `Corrupted`, type: 'unmet' })
  return <FormatSection lines={lines} />
}

function ItemTemplateExplicits({
  base,
  stats,
  corrupted,
  custom,
  mods
}: {
  base: ItemDefinition
  stats: Record<string, number | Range>
  corrupted?: boolean
  custom?: string[]
  mods?: UniqueRandomMod[]
}) {
  const lines = statLines('explicitMod', stats, itemTranslationDomain(base))
  if (custom) lines.push(...custom.map((text) => ({ text, type: 'explicitMod' })))
  const randomMods = mods?.filter((mod) => mod.statType !== 'implicit' && mod.statType !== 'enchant')
  if (randomMods?.length) {
    lines.push(
      ...randomMods.flatMap((mod) =>
        uniqueGroupName(mod).map((text) => ({
          text,
          type: `${mod.statType ?? 'explicit'}Mod`
        }))
      )
    )
  }
  if (corrupted) lines.push({ text: `Corrupted`, type: 'unmet' })
  return <FormatSection lines={lines} />
}

const ItemFlavourSection = ({ flavour, descr }: { flavour?: string; descr?: string }) => (
  <>
    {!!flavour && <FormatLineBr type="flavourText" text={flavour} />}
    {!!(flavour && descr) && <Separator />}
    {!!descr && <FormatLineBr type="descrText" text={descr} />}
  </>
)

function itemDescription(base: ItemDefinition) {
  const template = Data.item_templates[base.inherits]
  return template.description ?? base.properties?.directions
}

function ItemFlavourUnique({ item }: { item: Item }) {
  const id = Data.uniques[item.unique!]?.flavour || item.unique
  const flavour = Data.flavour[id!]
  const descr = itemDescription(Data.items[item.base])
  if (!flavour && !descr) return null
  return <ItemFlavourSection flavour={flavour} descr={descr} />
}

function ItemFlavour({ item }: { item: Item }) {
  if (item.unique) return <ItemFlavourUnique item={item} />
  const base = Data.items[item.base]
  const flavour = Data.flavour[base.visual_identity.id]
  const descr = itemDescription(base)
  return <ItemFlavourSection flavour={flavour} descr={descr} />
}

function ItemImage({ item }: { item: Item }) {
  const base = Data.items[item.base]
  const data = Data.uniques[item.unique!] ?? base
  if (!data) return null
  return <TooltipImage icon={data.icon} name={data.name} width={base.inventory_width} />
}

const itemType = (item: Item) => itemGetBaseColor(Data.items[item.base]) || item.rarity || 'normal'

function CardTooltip({ item }: { item: Item }) {
  const base = Data.items[item.base]
  if (!base) return <div />
  return (
    <GameTooltip className="poe-divinationCard">
      <div className="poe-cardFace">
        <img src={getIconPath(base.visual_identity.card_art!)} alt="" />
      </div>
      <div className="poe-itemBoxContent">
        <ItemHeader item={item} />
        <div className="poe-stackSize">
          {item.quantity || 1}/{base.properties.stack_size}
        </div>
        <div className="poe-itemCardInfo">
          <div className="poe-itemCardInfoTop">
            <FormatLine type="explicitMod" text={base.properties.description ?? ''} />
          </div>
          <div className="poe-itemCardInfoDivider" />
          <div className="poe-itemCardInfoBottom">
            <ItemFlavour item={item} />
          </div>
        </div>
      </div>
    </GameTooltip>
  )
}

function ItemSpecial({ item }: { item: Item }) {
  const type = Data.items[item.base]?.item_class
  if (type === 'MemoryLine') {
    return <FormatSection lines={[{ text: 'Unmodifiable', type: 'explicitMod' }]} />
  } else {
    return null
  }
}

export function ItemTooltip({
  item,
  template,
  options
}: {
  item: Item
  template?: boolean
  options?: ItemTooltipOptions
}) {
  const base = Data.items[item.base]
  if (!base) return null
  if (base.tags.includes('divination_card')) return <CardTooltip item={item} />
  return (
    <GameTooltip className={`poe-${itemType(item)}Popup`}>
      <ItemHeader item={item} />
      <div className="poe-content">
        {item.base !== 'Metadata/Items/Currency/CurrencyItemisedSextantModifier' && (
          <ItemProperties item={item} template={template} />
        )}
        <ItemEnchant item={item} options={options} />
        <ItemClusterJewel item={item} options={options} />
        {template ? <ItemTemplateImplicits base={base} /> : <ItemImplicits item={item} options={options} />}
        <ItemExplicits item={item} options={options} />
        <ItemSpecial item={item} />
        <ItemFlavour item={item} />
      </div>
      {!!(!options || options.art) && <ItemImage item={item} />}
    </GameTooltip>
  )
}

export function ItemTemplateTooltip({ name, variant }: { name: string; variant?: number }) {
  if (!Data.uniques[name]) {
    if (Data.items[name]?.skills) {
      const id = (variant && Data.items[name].skills![variant]) || Data.items[name].skills![0]
      return <GemTemplateTooltip id={id} />
    }
    const item = itemCreate(name)
    if (!item) return <div />
    item.rarity = 'normal'
    item.ilvl = 0
    item.quality = 0
    if (item.clusterJewel) {
      item.clusterJewel.skill = 'dummy_skill'
    }
    if (item.map_tier && variant) item.map_tier = variant
    return <ItemTooltip item={item} template />
  }

  const unique = Data.uniques[name]
  const variantData = (variant != null && unique.variants[variant]) || unique.variants[unique.variants.length - 1]
  const base = Data.items[variantData.base]
  const descr = itemDescription(base)

  return (
    <GameTooltip className="poe-uniquePopup">
      <ItemDoubleHeader name={unique.name} base={base.name} influence={unique.flags} />
      <div className="poe-content">
        <ItemTemplateProperties name={name} variant={variant} />
        <ItemTemplateImplicits base={base} mods={variantData.random_mods} />
        <ItemTemplateExplicits
          base={base}
          stats={variantData.stats}
          custom={variantData.custom}
          mods={variantData.random_mods}
          corrupted={(unique.flags & ItemFlags.Corrupted) !== 0}
        />
        {!!(Data.flavour[unique.flavour || name] || descr) && (
          <ItemFlavourSection flavour={Data.flavour[unique.flavour || name]} descr={descr} />
        )}
      </div>
      <TooltipImage icon={unique.icon} name={unique.name} />
    </GameTooltip>
  )
}

export function GemTooltip({ gem }: { gem: Gem }) {
  const props = gemFormatProperties(gem, gemGetProperties(gem, true))
  const data = Data.gems[gem.id]
  const icon = gemItemIcon(data)
  return (
    <GameTooltip className="poe-gemPopup">
      <ItemSingleHeader name={props.name} />
      <div className="poe-content">
        <FormatSection lines={props.properties} />
        <FormatSection lines={props.requirements} />
        <FormatSection lines={props.description} />
        <FormatSection lines={props.stats} />
        <FormatLine
          text="Place into an item socket of the right colour to gain this skill. Right click to remove from a socket."
          type="descrText"
        />
      </div>
      {icon && <TooltipImage icon={icon} name={props.name} />}
    </GameTooltip>
  )
}

export function GemTemplateTooltip({ id }: { id: string }) {
  const data = Data.gems[id]
  if (!data) return <div />
  const props = gemTemplateProps(id)!
  const icon = gemItemIcon(data)
  return (
    <GameTooltip className="poe-gemPopup">
      <ItemSingleHeader name={props.name} />
      <div className="poe-content">
        <FormatSection lines={props.properties} />
        <FormatSection lines={props.requirements} />
        <FormatSection lines={props.description} />
        <FormatSection lines={props.stats} />
        <FormatLine
          text="Place into an item socket of the right colour to gain this skill. Right click to remove from a socket."
          type="descrText"
        />
      </div>
      {icon && <TooltipImage icon={icon} name={props.name} />}
    </GameTooltip>
  )
}
